// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { ApiServiceError } from "./apiError";

export const COLD_START_RETRY_BUDGET_MS = 120_000;
export const COLD_START_BACKOFF_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 16_000];
export const COLD_START_UNAVAILABLE_STATUS = 503;
export const WAKING_UP_AFTER_MS = 3_000;

export type WarmUpFailure = "unreachable" | "signedOut" | "rejected";

const DELTA_SECONDS = /^\d+$/;

export interface ColdStartRetryOptions {
  deadline: number;
  signal: AbortSignal;
  isRetryable: (error: unknown) => boolean;
  canRetry: () => boolean;
  onRetry?: () => void;
}

export function isColdStartRetryable(error: unknown): boolean {
  if (error instanceof ApiServiceError) {
    return error.status === COLD_START_UNAVAILABLE_STATUS;
  }
  return (
    error instanceof TypeError ||
    (error instanceof DOMException && error.name === "TimeoutError")
  );
}

export function classifyWarmUpFailure(
  error: unknown,
  isRetryable: (error: unknown) => boolean,
): WarmUpFailure {
  if (error instanceof ApiServiceError && error.status === 401) {
    return "signedOut";
  }
  return isRetryable(error) ? "unreachable" : "rejected";
}

export function retryAfterDelayMs(error: unknown, now: number): number {
  if (!(error instanceof ApiServiceError) || error.retryAfter === undefined) {
    return 0;
  }
  const value = error.retryAfter.trim();
  if (DELTA_SECONDS.test(value)) {
    return Number(value) * 1_000;
  }
  const retryAt = Date.parse(value);
  return Number.isNaN(retryAt) ? 0 : Math.max(0, retryAt - now);
}

export function coldStartDelayMs(attempt: number, error: unknown, now: number): number {
  const backoff = COLD_START_BACKOFF_MS[Math.min(attempt, COLD_START_BACKOFF_MS.length - 1)]!;
  return Math.max(backoff, retryAfterDelayMs(error, now));
}

function waitOrAbort(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const finish = (): void => {
      clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    signal.addEventListener("abort", finish, { once: true });
  });
}

export async function retryColdStart<T>(
  attempt: () => Promise<T>,
  options: ColdStartRetryOptions,
): Promise<T> {
  for (let attemptIndex = 0; ; attemptIndex += 1) {
    try {
      return await attempt();
    } catch (error) {
      if (!options.canRetry() || !options.isRetryable(error)) {
        throw error;
      }
      const wait = coldStartDelayMs(attemptIndex, error, Date.now());
      if (Date.now() + wait >= options.deadline) {
        throw error;
      }
      options.onRetry?.();
      await waitOrAbort(wait, options.signal);
      if (!options.canRetry()) {
        throw error;
      }
    }
  }
}
